import React, { useState } from 'react';
import PositionItem from './PositionItem';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../utils/cn';

const { FiLayers, FiTrash2, FiCpu, FiUser } = FiIcons;

export default function PositionsList({
  positions,
  onManualSell,
  onUpdatePosition,
  onDeletePosition,
  onForceClose,
  onClearClosedPositions,
  botTimeframe
}) {
  const [statusFilter, setStatusFilter] = useState('ACTIVE'); // default to ACTIVE; 'ALL', 'ACTIVE', 'CLOSED'
  const [typeFilter, setTypeFilter] = useState('ALL'); // 'ALL', 'AUTO', 'MANUAL'

  const activeStatuses = ['ACTIVE', 'ERROR', 'CLOSING', 'PENDING', 'EXECUTING_BUY'];
  
  const activeCount = positions.filter(p => activeStatuses.includes(p.status)).length;
  const closedCount = positions.filter(p => p.status === 'CLOSED').length;

  const filteredPositions = positions.filter(p => {
    // Status match
    let sMatch = true;
    if (statusFilter === 'ACTIVE') sMatch = activeStatuses.includes(p.status);
    else if (statusFilter !== 'ALL') sMatch = p.status === statusFilter;

    // Origin/Type match
    let tMatch = true;
    if (typeFilter === 'AUTO') tMatch = !!p.botTriggered;
    else if (typeFilter === 'MANUAL') tMatch = !p.botTriggered;

    return sMatch && tMatch;
  });

  // Dynamic empty state message
  let emptyMessage = 'No positions tracked yet.';
  if (statusFilter !== 'ALL' || typeFilter !== 'ALL') {
    const typeStr = typeFilter === 'ALL' ? '' : typeFilter.toLowerCase() + ' ';
    const statusStr = statusFilter === 'ALL' ? '' : statusFilter.toLowerCase() + ' ';
    emptyMessage = `No ${typeStr}${statusStr}positions found.`;
  }

  return (
    <div className="glass-panel rounded-2xl p-6 h-auto lg:h-full flex flex-col lg:overflow-hidden">
      
      <div className="flex flex-col 2xl:flex-row items-start 2xl:items-center justify-between mb-6 flex-shrink-0 gap-4">
        {/* Header Title & Active Count */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiLayers} className="text-blue-400 text-xl" />
            <h2 className="text-lg font-bold text-white">Market Positions</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-slate-800 px-2 py-0.5 rounded-full text-[10px] font-bold text-slate-300 uppercase tracking-wider hidden sm:block">
              {activeCount} Active
            </div>
            {closedCount > 0 && (
              <button 
                onClick={onClearClosedPositions}
                className="bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors hidden sm:flex items-center gap-1"
                title="Clear all closed positions"
              >
                <SafeIcon icon={FiTrash2} /> Clear Closed
              </button>
            )}
          </div>
        </div>

        {/* Filter Toggles */}
        <div className="flex flex-wrap items-center gap-2 w-full 2xl:w-auto">
          {/* Status Filter */}
          <div className="flex items-center bg-[#0B0E14] p-1 rounded-lg border border-slate-800 flex-1 sm:flex-none">
            {['ALL', 'ACTIVE', 'CLOSED'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={cn(
                  "flex-1 sm:flex-none px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all",
                  statusFilter === f ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"
                )}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Trade Origin Type Filter */}
          <div className="flex items-center bg-[#0B0E14] p-1 rounded-lg border border-slate-800 flex-1 sm:flex-none">
            <button
              onClick={() => setTypeFilter('ALL')}
              className={cn(
                "flex-1 sm:flex-none px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all",
                typeFilter === 'ALL' ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"
              )}
            >
              ANY
            </button>
            <button
              onClick={() => setTypeFilter('AUTO')}
              className={cn(
                "flex-1 sm:flex-none px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 border",
                typeFilter === 'AUTO' ? "bg-blue-500/20 text-blue-400 border-blue-500/20 shadow-sm" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 border-transparent"
              )}
            >
              <SafeIcon icon={FiCpu} /> AUTO
            </button>
            <button
              onClick={() => setTypeFilter('MANUAL')}
              className={cn(
                "flex-1 sm:flex-none px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 border",
                typeFilter === 'MANUAL' ? "bg-slate-700 text-white border-slate-600 shadow-sm" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 border-transparent"
              )}
            >
              <SafeIcon icon={FiUser} /> MANUAL
            </button>
          </div>
        </div>
      </div>

      {/* Positions List */}
      <div className="flex-1 overflow-visible lg:overflow-y-auto pr-2 space-y-4 custom-scrollbar">
        <AnimatePresence mode="popLayout">
          {filteredPositions.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center text-slate-600 py-20"
            >
              <SafeIcon icon={FiLayers} className="text-5xl opacity-10 mb-4" />
              <p className="text-sm font-medium">
                {emptyMessage}
              </p>
              {statusFilter === 'ALL' && typeFilter === 'ALL' && (
                <p className="text-xs mt-1">Paste a token address on the left to start.</p>
              )}
            </motion.div>
          ) : (
            filteredPositions.map(pos => (
              <PositionItem
                key={pos.id}
                position={pos}
                onManualSell={onManualSell}
                onUpdatePosition={onUpdatePosition}
                onDeletePosition={onDeletePosition}
                onForceClose={onForceClose}
                botTimeframe={botTimeframe}
              />
            ))
          )}
        </AnimatePresence>
      </div>

    </div>
  );
}